import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useTheme } from "../common/ThemeContext";
import { fetchAuth } from "../common/api";
import { formatLocalDate } from "../common/datetimeUtils";

const SectionCard = ({ title, right, children }) => {
  const { isDarkMode } = useTheme();
  return (
    <section
      className={`rounded-2xl p-6 shadow-lg border ${
        isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
};

SectionCard.propTypes = {
  title: PropTypes.string.isRequired,
  right: PropTypes.node,
  children: PropTypes.node,
};

export default function InjuryTab({ playerId }) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  const [injuries, setInjuries] = useState([]);
  const [injForm, setInjForm] = useState({
    side: "Left",
    region: "",
    type: "",
    injury_date: "",
    duration_weeks: "",
  });

  // API
  const listInjuries = async (pid) => {
    const res = await fetchAuth(`/players/${pid}/injuries`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Error list_injuries");
    return data;
  };

  const createInjury = async (pid, payload) => {
    const res = await fetchAuth(`/players/${pid}/injuries`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Error create_injury");
    return data;
  };

  // Load
  useEffect(() => {
    (async () => {
      try {
        const list = await listInjuries(playerId);
        setInjuries(list);
      } catch (e) {
        
      }
    })();
  }, [playerId]);

  const onChangeInj = (k, v) => setInjForm((s) => ({ ...s, [k]: v }));

  const submitInjury = async () => {
    if (!injForm.region || !injForm.type || !injForm.injury_date || !injForm.duration_weeks) return;
    const payload = {
      side: injForm.side,
      region: injForm.region,
      type: injForm.type,
      injury_date: injForm.injury_date,
      duration_weeks: Number(injForm.duration_weeks),
    };
    const saved = await createInjury(playerId, payload);
    setInjuries((prev) => [saved, ...prev]);
    setInjForm({ side: "Left", region: "", type: "", injury_date: "", duration_weeks: "" });
  };

  return (
    <div className="space-y-8">
      <SectionCard title={t("staff_update.add_injury", { defaultValue: "Add injury" })}>
        <div className="grid md:grid-cols-5 gap-4">
          <div>
            <label className="block mb-1 font-medium">{t("staff_update.side", { defaultValue: "Side" })}</label>
            <select
              value={injForm.side}
              onChange={(e) => onChangeInj("side", e.target.value)}
              className={`w-full rounded-lg p-2 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-300"
              }`}
            >
              <option>{t("player_identity.left", { defaultValue: "Left" })}</option>
              <option>{t("player_identity.right", { defaultValue: "Right" })}</option>
              <option>{t("staff_update.bilateral", { defaultValue: "Bilateral" })}</option>
            </select>
          </div>

          <div>
            <label className="block mb-1 font-medium">{t("staff_update.region", { defaultValue: "Region" })}</label>
            <input
              value={injForm.region}
              onChange={(e) => onChangeInj("region", e.target.value)}
              placeholder={t("staff_update.region_placeholder", { defaultValue: "knee, ankle, hamstring..." })}
              className={`w-full rounded-lg p-2 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                isDarkMode ? "bg-gray-900 border-gray-700 placeholder-gray-500" : "bg-white border-gray-300 placeholder-gray-400"
              }`}
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">{t("staff_update.type", { defaultValue: "Type" })}</label>
            <input
              value={injForm.type}
              onChange={(e) => onChangeInj("type", e.target.value)}
              placeholder={t("staff_update.type_placeholder", { defaultValue: "tear, sprain..." })}
              className={`w-full rounded-lg p-2 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                isDarkMode ? "bg-gray-900 border-gray-700 placeholder-gray-500" : "bg-white border-gray-300 placeholder-gray-400"
              }`}
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">{t("staff_update.injury_date", { defaultValue: "Injury date" })}</label>
            <input
              type="date"
              value={injForm.injury_date}
              onChange={(e) => onChangeInj("injury_date", e.target.value)}
              className={`w-full rounded-lg p-2 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-300"
              }`}
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">{t("staff_update.duration_weeks", { defaultValue: "Duration (weeks)" })}</label>
            <input
              type="number"
              min={0}
              value={injForm.duration_weeks}
              onChange={(e) => onChangeInj("duration_weeks", e.target.value)}
              className={`w-full rounded-lg p-2 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-300"
              }`}
            />
          </div>
        </div>

        <button
          onClick={submitInjury}
          className={`mt-6 px-6 py-2 rounded-xl font-semibold text-white shadow focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            isDarkMode ? "bg-indigo-600 hover:bg-indigo-700 focus:ring-offset-gray-900" : "bg-indigo-600 hover:bg-indigo-700 focus:ring-offset-white"
          }`}
          disabled={!injForm.region || !injForm.type || !injForm.injury_date || !injForm.duration_weeks}
        >
          {t("staff_update.save_injury", { defaultValue: "Save injury" })}
        </button>
      </SectionCard>

      <SectionCard title={t("staff_update.injuries_history", { defaultValue: "Injuries history" })}>
        {injuries.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
                <tr>
                  <th className="text-left py-2 pr-4">{t("table.date")}</th>
                  <th className="text-left py-2 pr-4">{t("staff_update.side", { defaultValue: "Side" })}</th>
                  <th className="text-left py-2 pr-4">{t("staff_update.region", { defaultValue: "Region" })}</th>
                  <th className="text-left py-2 pr-4">{t("staff_update.type", { defaultValue: "Type" })}</th>
                  <th className="text-left py-2 pr-4">{t("staff_update.duration_short", { defaultValue: "Duration (w)" })}</th>
                  <th className="text-left py-2">{t("staff_update.note", { defaultValue: "Note" })}</th>
                </tr>
              </thead>
              <tbody>
                {injuries.map((inj) => (
                  <tr
                    key={inj.id}
                    className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                  >
                    <td className="py-2 pr-4">{formatLocalDate(inj.injury_date)}</td>
                    <td className="py-2 pr-4">{inj.side}</td>
                    <td className="py-2 pr-4">{inj.region}</td>
                    <td className="py-2 pr-4">{inj.type}</td>
                    <td className="py-2 pr-4">{inj.duration_weeks}</td>
                    <td className="py-2">
                      {inj.note ? (
                        <span className="truncate inline-block max-w-xs">{inj.note}</span>
                      ) : (
                        <em className="text-gray-500">—</em>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">{t("staff_update.no_injuries_yet", { defaultValue: "No injuries yet." })}</p>
        )}
      </SectionCard>
    </div>
  );
}

InjuryTab.propTypes = {
  playerId: PropTypes.string.isRequired,
};
